/**
 * usePrefetchNextScreenImages — подгрузить картинки следующего экрана, пока
 * покупатель стоит на текущем (WEB-1995).
 *
 * Зачем: сегодня картинка экрана начинает грузиться только после того, как
 * экран отрисован — покупатель нажал ответ, увидел пустое место, и лишь потом
 * картинку. На мобильной сети это секунда-две на каждом экране. Выигрыш здесь
 * в ОЩУЩАЕМОЙ скорости перехода, а не в Core Web Vitals: LCP считают только
 * для первой загрузки страницы, переходы внутри воронки в него не попадают.
 *
 * Три осторожности, без которых предзагрузка вредит:
 *   1. Стартуем ПОСЛЕ загрузки картинок текущего экрана — иначе отбираем
 *      полосу у его собственного показа и ухудшаем ровно то, что улучшаем.
 *   2. Уважаем сеть: Save-Data и 2g — не грузим вовсе, 3g — по одной картинке
 *      на кандидата.
 *   3. В превью конструктора молчим: там это мусор в сети и ложные замеры.
 *
 * Почему `new Image()`, а не `<link rel="prefetch" as="image">`: Safari
 * `rel=prefetch` не поддерживает (ни на macOS, ни на iOS), а на мобильной
 * воронке Safari — половина трафика. `new Image()` работает везде одинаково,
 * попадает в обычный кеш картинок и при переходе отдаётся из памяти. Низкий
 * приоритет задаётся `fetchpriority="low"` (там, где браузер его понимает;
 * где нет — запрос всё равно уходит после загрузки текущего экрана).
 */
import { useEffect, useMemo } from 'react';
import type { EditorScreen } from '@/store/editorSlice';
import type { EvalContext, Logic } from '@/logic-engine';
import type { VariableContext } from '@/player/variable-context';
import type { SyncSources } from '@/player/sources';
import { useEditorPreview } from '@/components/editor/EditorPreviewContext';
import {
  nextScreenImageCandidates,
  PREFETCH_MAX_IMAGES,
  type NextScreenImagePlan,
} from './nextScreenImageCandidates';

/**
 * Сколько ждём простоя браузера, прежде чем начать. Таймаут нужен для вкладок,
 * которые простоя не дождутся вовсе (анимация, таймер на экране): через
 * секунду грузим всё равно — картинки текущего экрана к этому моменту уже
 * загружены, полосу мы у них не отбираем.
 */
const IDLE_TIMEOUT_MS = 1000;

/**
 * Адреса, уже отданные браузеру в этой сессии страницы. Модульный уровень
 * намеренно: это память ВКЛАДКИ, а не компонента — покупатель ходит по
 * экранам вперёд и назад, визард перемонтируется, и без общей памяти один и
 * тот же адрес запрашивался бы на каждом возврате.
 */
const prefetchedUrls = new Set<string>();

/** Только для тестов: забыть, что уже грузили. */
export function __resetPrefetchMemoryForTests(): void {
  prefetchedUrls.clear();
}

export interface UsePrefetchNextScreenImagesOptions {
  screens: readonly EditorScreen[];
  /** Индекс экрана, который сейчас на глазах у покупателя (safeIndex). */
  currentIndex: number;
  logic: Logic;
  /** Тот же источник ответов/параметров, что кормит hiddenSet визарда. */
  varCtx: Pick<VariableContext, 'answers' | 'urlParams'>;
  variables?: Record<string, string>;
  geoCountry?: string | null;
  syncSources: Pick<SyncSources, 'deviceOs' | 'browser' | 'browserInApp'>;
  paymentMethod?: string[];
  /** Первый экран пейволла — ступень по умолчанию с последнего экрана квиза. */
  paywallFirstScreen?: EditorScreen | null;
  sizeOfImage?: (url: string) => number | undefined;
}

interface NetworkPlan {
  /** Предзагрузку не делаем вовсе. */
  skip: boolean;
  /** Сколько картинок брать с одного кандидата. */
  maxPerCandidate: number;
}

/**
 * Что позволяет сеть. `navigator.connection` — нестандартное поле (нет в
 * Safari), поэтому его отсутствие читается как «ограничений не знаем»:
 * ведём себя как на быстрой сети, но с общими лимитами по числу и объёму.
 */
function readNetworkPlan(): NetworkPlan {
  const connection = (
    navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string };
    }
  ).connection;
  if (!connection)
    return { skip: false, maxPerCandidate: Number.POSITIVE_INFINITY };
  if (connection.saveData === true) return { skip: true, maxPerCandidate: 0 };
  const type = connection.effectiveType;
  if (type === '2g' || type === 'slow-2g')
    return { skip: true, maxPerCandidate: 0 };
  if (type === '3g') return { skip: false, maxPerCandidate: 1 };
  return { skip: false, maxPerCandidate: Number.POSITIVE_INFINITY };
}

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  cancelIdleCallback?: (handle: number) => void;
};

/** Запланировать работу на простое браузера; вернуть отмену. */
function scheduleIdle(run: () => void): () => void {
  const w = window as IdleWindow;
  if (typeof w.requestIdleCallback === 'function') {
    const handle = w.requestIdleCallback(run, { timeout: IDLE_TIMEOUT_MS });
    return () => w.cancelIdleCallback?.(handle);
  }
  const timer = window.setTimeout(run, 0);
  return () => window.clearTimeout(timer);
}

/**
 * Отдать адрес браузеру. Второй раз за сессию один и тот же адрес не грузим.
 *
 * Ревизия пачки 29, п.7: адрес помечался загруженным ДО того, как загрузка
 * состоялась, — сорвавшаяся предзагрузка (обрыв мобильной сети) навсегда
 * оставалась «уже загруженной», и повторной попытки не было НИКОГДА:
 * покупатель на этом экране снова ждал картинку, то есть ровно то, ради чего
 * хук и написан. Теперь метка живёт по факту: ставится сразу (чтобы два
 * одновременных запроса одного адреса не ушли), но СНИМАЕТСЯ по `error` —
 * и остаётся только у того адреса, который действительно доехал (`load`).
 */
function prefetchImage(url: string, priority: 'low' | 'high'): void {
  if (prefetchedUrls.has(url)) return;
  // Пока запрос в полёте, адрес считается занятым — иначе две подсказки
  // подряд (простой + палец на варианте) отправили бы его дважды.
  prefetchedUrls.add(url);
  const img = new window.Image() as HTMLImageElement & {
    fetchPriority?: string;
  };
  img.decoding = 'async';
  img.fetchPriority = priority;
  (
    img as { setAttribute?: (name: string, value: string) => void }
  ).setAttribute?.('fetchpriority', priority);
  img.onload = () => {
    // Подтверждение: адрес в кеше картинок браузера, второй раз не нужен.
    prefetchedUrls.add(url);
  };
  img.onerror = () => {
    // Не доехал — отпускаем, чтобы следующая попытка (возврат на экран,
    // палец на варианте) сходила ещё раз.
    prefetchedUrls.delete(url);
  };
  img.src = url;
}

/**
 * Дождаться, пока картинки текущего экрана догрузятся, и позвать `run`.
 * Возвращает отмену (снимает слушатели и отменяет простой).
 */
function whenScreenImagesSettled(run: () => void): () => void {
  // Ревизия пачки 29, п.3 (WEB-1995 × WEB-1994): ЛЕНИВЫЕ картинки из
  // ожидания исключены. `loading="lazy"` (галерея ставит его с четвёртой
  // плитки) за пределами экрана браузером не запрашивается вовсе — ни
  // `load`, ни `error` от неё не будет НИКОГДА, счётчик ожидания не дошёл бы
  // до нуля, и предзагрузка не стартовала бы ни разу. Полосу они у текущего
  // экрана и не отбирают — по построению, их никто ещё не грузит.
  const pending = Array.from(document.images).filter(
    (img) => !img.complete && img.loading !== 'lazy',
  );
  let cancelIdle: (() => void) | null = null;
  let cancelled = false;
  let started = false;
  let deadline: number | null = null;

  const start = (): void => {
    if (cancelled || started) return;
    started = true;
    if (deadline !== null) window.clearTimeout(deadline);
    cancelIdle = scheduleIdle(run);
  };

  if (pending.length === 0) {
    start();
    return () => {
      cancelled = true;
      cancelIdle?.();
    };
  }

  let left = pending.length;
  const onSettled = (): void => {
    left -= 1;
    if (left <= 0) start();
  };
  pending.forEach((img) => {
    img.addEventListener('load', onSettled, { once: true });
    // Картинка, которая не загрузилась, ждать себя дальше не заставляет:
    // без этого один битый адрес запирал бы предзагрузку навсегда.
    img.addEventListener('error', onSettled, { once: true });
  });
  // Ревизия пачки 29, п.3 (второй конец): ОБЩИЙ потолок ожидания. Список
  // исключений «кто может не доложить о себе» ручным быть не должен —
  // ленивые сегодня, завтра любая другая причина молчания (картинка, снятая
  // из разметки до события; источник, который браузер отложил сам). Через
  // IDLE_TIMEOUT_MS стартуем в любом случае: к этой секунде текущий экран
  // покупателю уже показан, отбирать у него полосу нечем.
  deadline = window.setTimeout(start, IDLE_TIMEOUT_MS);

  return () => {
    cancelled = true;
    if (deadline !== null) window.clearTimeout(deadline);
    pending.forEach((img) => {
      img.removeEventListener('load', onSettled);
      img.removeEventListener('error', onSettled);
    });
    cancelIdle?.();
  };
}

export function usePrefetchNextScreenImages(
  options: UsePrefetchNextScreenImagesOptions,
): void {
  const {
    screens,
    currentIndex,
    logic,
    varCtx,
    variables,
    geoCountry,
    syncSources,
    paymentMethod,
    paywallFirstScreen,
    sizeOfImage,
  } = options;

  // Превью конструктора — единственный сигнал «я внутри канвы редактора»
  // (WEB-681/683). Публичное превью черновика (?preview=1) им не считается:
  // там ходит живой человек, и ускорение переходов ему нужно так же.
  const { isEditorPreview } = useEditorPreview();

  const plan = useMemo<NextScreenImagePlan & { skip: boolean }>(() => {
    if (isEditorPreview || typeof window === 'undefined') {
      return { candidates: [], urls: [], skip: true };
    }
    const network = readNetworkPlan();
    if (network.skip) return { candidates: [], urls: [], skip: true };
    const context: EvalContext = {
      answers: varCtx.answers,
      variables: variables ?? {},
      urlParams: varCtx.urlParams,
      geoCountry: geoCountry ?? null,
      deviceOs: syncSources.deviceOs,
      browser: syncSources.browser,
      browserInApp: syncSources.browserInApp,
      paymentMethod: paymentMethod ?? [],
    };
    const computed = nextScreenImageCandidates({
      screens,
      currentIndex,
      logic,
      context,
      paywallFirstScreen,
      sizeOfImage,
      maxPerCandidate: network.maxPerCandidate,
    });
    return { ...computed, skip: false };
  }, [
    isEditorPreview,
    screens,
    currentIndex,
    logic,
    varCtx,
    variables,
    geoCountry,
    syncSources,
    paymentMethod,
    paywallFirstScreen,
    sizeOfImage,
  ]);

  // Сама предзагрузка: после загрузки картинок текущего экрана, на простое.
  useEffect(() => {
    if (plan.skip || plan.urls.length === 0) return;
    if (typeof document === 'undefined') return;
    return whenScreenImagesSettled(() => {
      plan.urls.forEach((url) => prefetchImage(url, 'low'));
    });
  }, [plan]);

  // Подсказка по намерению: палец уже опустился на вариант ответа — грузим
  // картинки ИМЕННО его ветви и высоким приоритетом, не дожидаясь простоя.
  // Слушатель один на документ, на фазе перехвата: варианты рисует общий
  // рендер блоков (PublicBlockRenderer), и отдельный обработчик на каждом
  // варианте пришлось бы протаскивать через все места его вызова.
  useEffect(() => {
    if (plan.skip || plan.candidates.length === 0) return;
    if (typeof document === 'undefined') return;
    const onPointerDown = (event: Event): void => {
      const target = event.target as Element | null;
      const optionEl = target?.closest?.('[data-testid^="option-"]') ?? null;
      const testId = optionEl?.getAttribute('data-testid') ?? '';
      if (!testId.startsWith('option-')) return;
      const optionId = testId.slice('option-'.length);
      const branch = plan.candidates.find((c) =>
        c.optionIds.includes(optionId),
      );
      if (!branch) return;
      branch.urls
        .slice(0, PREFETCH_MAX_IMAGES)
        .forEach((url) => prefetchImage(url, 'high'));
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () =>
      document.removeEventListener('pointerdown', onPointerDown, true);
  }, [plan]);
}
